"use client";

import React from "react";
import { motion } from "framer-motion";

import Image from "next/image";
import { cn } from "@/lib/utils";

import { GroqImage } from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

import check from "@/public/frankfurt-event/check.svg";

export default function Schedule({
  data,
}: {
  data: PageEventSchema | PageKryptoDaySchema;
}) {
  return (
    <section className="relative pb-[10rem] pt-[4rem] overflow-clip lg:pb-[26rem] lg:pt-[10rem]">
      <div className="px-[2rem] w-[min(152.5rem,100%)] mx-auto flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center w-[min(100rem,100%)] mb-[4rem] lg:mb-[12rem]"
        >
          {data?.overviewTexts && (
            <PortableText
              value={data?.overviewTexts ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="gradient-title !text-white/80 relative text-[4rem] lg:text-[7rem] leading-[1.1em] font-[600] tracking-[-.14rem] text-white text-center mb-[1.5rem] lg:mb-[2rem]">
                      {children}
                    </h2>
                  ),
                  h6: ({ children }) => (
                    <div className="mx-auto font-bold uppercase bg-white bg-opacity-10 rounded-full border border-white border-opacity-20 w-fit px-[2rem] py-[1.5rem] leading-[1rem] text-[1.75rem] backdrop-blur-[2rem] mb-[2rem]">
                      {children}
                    </div>
                  ),
                  normal: ({ children }) => (
                    <p className="text-white text-opacity-75">{children}</p>
                  ),
                },
              })}
            />
          )}
        </motion.div>

        <div className="relative w-full ps-[2rem] lg:ps-0">
          <div className="flex absolute top-0 left-0 justify-center h-full transform -translate-x-1/2 lg:left-1/2 overflow-clip">
            <div className="absolute h-full bg-opacity-20 rounded-full w-[.25rem] bg-[#61FFCE]"></div>
            <div className="sticky flex-shrink-0 top-[80vh] w-[2rem] h-[2rem] before:absolute before:h-[200vh] before:w-[.25rem] before:bottom-[2rem] before:rounded-full before:bg-gradient-to-t before:from-[#61FFCE] before:-translate-x-1/2 before:left-1/2">
              <div className="rotate-45 w-[1rem] h-[1rem] bg-[#61FFCE] translate-x-[.5rem] translate-y-[.18rem] shadow-[0_0_6rem_rgba(55,210,1,.15)]"></div>
            </div>
          </div>

          {data?.overviewCards &&
            data?.overviewCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="flex flex-col lg:grid lg:grid-cols-2 pb-[8rem] lg:pb-[20rem] last:!pb-[5rem]"
              >
                <div
                  className={
                    (cn("flex flex-col items-start justify-center"),
                    index % 2 !== 0 ? "lg:ps-[16rem]" : "")
                  }
                >
                  <div className="w-[min(57rem,100%)] flex flex-col items-start">
                    {card?.texts && (
                      <PortableText
                        value={card?.texts ?? []}
                        components={customRichTextComponent({
                          block: {
                            h2: ({ children }) => (
                              <h2 className="w-[min(57rem,100%)] text-[4rem] font-bold text-white leading-[1.2em] mb-[2rem]">
                                {children}
                              </h2>
                            ),
                            h6: ({ children }) => (
                              <div className="font-bold uppercase bg-white bg-opacity-10 rounded-full border border-white border-opacity-20 w-fit px-[2rem] py-[1.5rem] leading-[1rem] text-[1.75rem] backdrop-blur-[2rem] mb-[2rem]">
                                {children}
                              </div>
                            ),
                            normal: ({ children }) => (
                              <p className="font-bold text-[2rem] text-white/90 leading-[1.2em] mb-[2rem]">
                                {children}
                              </p>
                            ),
                          },
                        })}
                      />
                    )}

                    <GroqImage
                      className="!w-[min(57rem,100%)] drop-shadow-[0_0_4rem_rgba(55,210,1,.15)] mb-[2rem] lg:hidden"
                      src={card?.image}
                      quality={100}
                      alt="card"
                    />

                    <ul className="flex flex-col mt-[2rem] gap-[2.5rem]">
                      {card?.pills &&
                        card?.pills?.map((pill, index) => (
                          <li
                            key={index + 100}
                            className="flex items-center gap-[1.5rem]"
                          >
                            <Image
                              className="w-[4rem] max-w-[4rem] min-w-[4rem]"
                              src={check}
                              alt="check"
                            />
                            <span className="mb-0 text-opacity-80 text-[#DADADA] leading-[normal]">
                              {pill}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>

                <div
                  className={cn(
                    "hidden lg:flex flex-col items-end justify-center",
                    index % 2 === 0
                      ? "order-last ps-[16rem]"
                      : "order-first pe-[16rem]",
                  )}
                >
                  <GroqImage
                    className="!w-[min(57rem,100%)] drop-shadow-[0_0_4rem_rgba(55,210,1,.15)]"
                    src={card?.image}
                    quality={100}
                    alt="card"
                  />
                </div>
              </motion.div>
            ))}
        </div>

        {data?.overviewCTA && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="[&_*]:font-sans [&_*]:uppercase"
          >
            <CTAButton
              data={data?.overviewCTA}
              className="[&_.cta]:!py-[2.5rem] mt-[5rem]"
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
